import React from 'react';
import styled from 'styled-components';
import '@fontsource/ibm-plex-mono';
import CustomSVG from './CustomSVG';

const Container = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 24px;
  max-width: 600px;
  background: linear-gradient(135deg, #f8faf9 0%, #f0f4f2 100%);
  border-radius: 16px;
  gap: 24px;
  position: relative;
  transition: all 0.3s ease;
  min-height: 180px;
  box-shadow: 0 2px 12px rgba(59, 98, 85, 0.08);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(59, 98, 85, 0.15);
    background: linear-gradient(135deg, #ffffff 0%, #f8faf9 100%);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 20px;
    min-height: auto;
    gap: 16px;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  flex-shrink: 0;
  align-self: center;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    align-self: center;
  }
`;

const BookImage = styled.img`
  height: 160px;
  width: 110px;
  object-fit: cover;
  border-radius: 12px;
  background: linear-gradient(45deg, #f0f0f0, #e0e0e0);
  
  @media (max-width: 768px) {
    height: 140px;
    width: 95px;
  }
`;

const ImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(59, 98, 85, 0) 0%,
    rgba(59, 98, 85, 0.1) 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  
  ${ImageContainer}:hover & {
    opacity: 1;
  }
`;

const Info = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24.5px;
  min-width: 0;
  
  @media (max-width: 768px) {
    padding-right: 0;
    width: 100%;
    align-items: center;
  }
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 20px;
  font-family: 'IBM Plex Mono';
  color: #2d3748;
  line-height: 1.4;
  padding-right: 60px;
  
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  
  min-height: 2.8em;
  
  @media (max-width: 768px) {
    font-size: 18px;
    text-align: center;
    min-height: 2.5em;
  }
`;

const Author = styled.div`
  font-size: 16px;
  font-family: 'IBM Plex Mono';
  color: #718096;
  font-weight: 500;
  
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: auto;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const Price = styled.div`
  color: #AE0505;
  font-size: 26px;
  font-weight: 800;
  font-family: 'IBM Plex Mono';
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(174, 5, 5, 0.1);
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const CartButton = styled.button<{disabled?: boolean}>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 24px;
  border: 2px solid #A13C17;
  border-radius: 25px;
  background: linear-gradient(135deg, #ffffff 0%, #fefefe 100%);
  font-family: 'IBM Plex Mono';
  font-weight: 600;
  font-size: 14px;
  color: #A13C17;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(161, 60, 23, 0.15);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #A13C17 0%, #8b331e 100%);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(161, 60, 23, 0.3);
    border-color: #8b331e;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(161, 60, 23, 0.2);
  }

  CustomSvg {
    width: 18px;
    height: 18px;
    transition: filter 0.3s ease;
  }
  
  &:hover:not(:disabled) svg path {
    fill: white;
  }
  
  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 13px;
    width: 100%;
    justify-content: center;
    position: static;
    right: auto;
    bottom: auto;
  }

  ${({ disabled }) =>
  disabled &&
  `
    cursor: not-allowed;
    opacity: 0.6;
  `}
`;

const DeleteIcon = styled.div`
  width: 32px;
  height: 32px;
  position: absolute;
  top: 24px;
  right: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(4px);
  
  &:hover {
    transform: scale(1.1);
    background: rgba(174, 5, 5, 0.1);
    box-shadow: 0 4px 12px rgba(174, 5, 5, 0.2);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  img {
    width: 18px;
    height: 18px;
    transition: filter 0.3s ease;
  }
  
  &:hover img {
    filter: brightness(0.7) sepia(1) hue-rotate(340deg) saturate(4) brightness(0.8);
  }
  
  @media (max-width: 768px) {
    top: 20px;
    right: 20px;
    width: 28px;
    height: 28px;
    
    img {
      width: 16px;
      height: 16px;
    }
  }
`;

interface WishlistItemProps {
  image: string;
  title: string;
  author: string;
  price: number;
  addedToCart: boolean;
  onAddToCart: () => void;
  onDelete: () => void;
}

const WishlistItem: React.FC<WishlistItemProps> = ({
  image,
  title,
  author,
  price,
  addedToCart,
  onAddToCart,
  onDelete,
}) => {
  return (
    <Container>
      <ImageContainer>
        <BookImage src={image} alt={title} />
        <ImageOverlay />
      </ImageContainer>
      <Info>
        <Title>
          {title}
          <DeleteIcon onClick={onDelete}>
            <img src="/media/DeleteIcon.svg" alt="Видалити" />
          </DeleteIcon>
        </Title>
        <Author>{author}</Author>
        <Actions>
          <Price>{price} грн</Price>
          <CartButton onClick={onAddToCart} disabled={addedToCart}>
            <CustomSVG
              src={`${process.env.PUBLIC_URL}/media/Cart.svg`}
              width="20px"
              height="20px"
              color="#8b331e"
            />
            {addedToCart ? 'Додано в кошик' : 'Додати в кошик'}
          </CartButton>
        </Actions>
      </Info>
    </Container>
  );
};

export default WishlistItem;